"use client";

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";

interface CarouselStateContext {
  defaultCarouselState: React.RefObject<Record<string, number>>;
}

const carouselStateContext = createContext<CarouselStateContext | undefined>({
  defaultCarouselState: { current: {} },
});

export function CarouselStateProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const ref = useRef<Record<string, number>>({});

  const contextValue = {
    defaultCarouselState: ref,
  };

  return (
    <carouselStateContext.Provider value={contextValue}>
      {children}
    </carouselStateContext.Provider>
  );
}

export function useCarouselState({ key }: { key: string }): {
  activeIndex: number;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
} {
  const context = useContext(carouselStateContext);
  if (!context) {
    throw new Error(
      "useCarouselState must be used within a CarouselStateProvider",
    );
  }

  const [activeIndex, setActiveIndex] = useState(
    context.defaultCarouselState.current[key] || 0,
  );

  return {
    activeIndex,
    setActiveIndex: useCallback(
      (newIndex: React.SetStateAction<number>) => {
        if (typeof newIndex === "number") {
          context.defaultCarouselState.current[key] = newIndex;
          setActiveIndex(newIndex);
        } else {
          setActiveIndex((prevIndex) => {
            const updatedIndex =
              typeof newIndex === "function" ? newIndex(prevIndex) : prevIndex;
            context.defaultCarouselState.current[key] = updatedIndex;
            return updatedIndex;
          });
        }
      },
      [context, key],
    ),
  };
}
